import { DateObject } from "react-multi-date-picker";
import { IAccountConveyanceReportResposne, ITeamMembersConveyanceList } from "../../../../Types/TeamMembers";

export const getMonthRange = (year?: number, month?: number) => {
  const today = new Date();
  const y = year ?? today.getFullYear();
  const m = month ?? today.getMonth() + 1; // JS months are 0-based
  const firstDay = new Date(y, m - 1, 1);
  const lastDay = new Date(y, m, 0);
  const start = new DateObject(firstDay);
  const end = new DateObject(lastDay);
  return [start, end] as (DateObject | null)[];
};

export const buildConveyanceApprovalPayload = (
  rows:
    | ITeamMembersConveyanceList
    | ITeamMembersConveyanceList[]
    | IAccountConveyanceReportResposne 
    | IAccountConveyanceReportResposne[],
  type: "Approve" | "Reject",
  role: string
) => {
  const extractNumericId = (code: string) =>
    Number(code?.replace(/\D/g, "") || 0);

  // Ensure we always work with an array
  const list = Array.isArray(rows) ? rows : [rows];

  // CASE 1: Team Members (each row directly has conveyance_code)
  if ("conveyance_code" in list[0]) {
    const conveyances = (list as ITeamMembersConveyanceList[]).map((r) => ({
      id: extractNumericId(r?.conveyance_code ?? ""),
      approved_amount: Number(r.approved_amount ?? r.amount ?? 0),
      remark: `${type}d after ${
        type === "Approve" ? "verification" : "review"
      }`,
    }));

    return {
      approval_type: role?.toLowerCase() || "manager",
      status: type === "Approve" ? "approved" : "rejected",
      conveyances: [{ conveyances }],
    };
  }

  // CASE 2: Account Reports (each row has nested details)
  if ("details" in list[0]) {
    const conveyances = (list as IAccountConveyanceReportResposne[])
      .flatMap((r) => r.details)
      .map((d) => ({
        id: extractNumericId(d?.conveyance_code ?? ""),
        approved_amount: Number(d.approved_amount ?? d.amount ?? 0),
        remark: `${type}d after ${
          type === "Approve" ? "verification" : "review"
        }`,
      }));

    return {
      approval_type: role?.toLowerCase() || "accounts",
      status: type === "Approve" ? "approved" : "rejected",
      conveyances: [{ conveyances }],
    };
  }

  // Fallback (shouldn’t occur)
  return {
    approval_type: role?.toLowerCase() || "manager",
    status: type === "Approve" ? "approved" : "rejected",
    conveyances: [],
  };
};

export const toNum = (v: unknown) => (v == null || v === "" ? 0 : Number(v));

export const summarizeByMember = (rows: ITeamMembersConveyanceList[]) => {
  const byId = rows.reduce((acc, r) => {
    const id = r?.team_member?.id ?? "unknown"; // 👈 fallback key
    if (!acc[id]) {
      acc[id] = {
        id,
        name: r.team_member?.name ?? "Unknown",
        totalAmount: 0,
        totalApproved: 0,
        totalCount: 0,
      };
    }
    acc[id].totalAmount += toNum(r.amount);
    acc[id].totalApproved += toNum(r.approved_amount);
    acc[id].totalCount += 1;
    return acc;
  }, {} as Record<string, { id: string; name: string; totalAmount: number; totalApproved: number; totalCount: number }>);

  return Object.values(byId);
};
